import { useEffect, useRef } from 'react';
import { useQuizMetaContext, useFollowerQuizGameState } from '@mentimeter/quiz';
import { GameStateEnums, type QuizResult } from '@mentimeter/quiz-types';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { Lobby } from '../../shared/components/quiz/lobby';
import { GetReady } from '../../shared/components/quiz/get-ready';
import { Countdown } from '../../shared/components/quiz/countdown';
import { Error } from '../../shared/components/quiz/error';
import { Loading } from '../../shared/components/quiz/loading';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { QuizBarsDataHandler } from './QuizBarsDataHandler';
import type { QuizChoicesPresentationProps } from './types';

interface FollowerPresentationProps extends QuizChoicesPresentationProps {
  activeSession: number;
  imageUrl: string | undefined;
  timeBasedScoring: boolean;
  shouldShowLegacyImage: boolean;
  isPreviewResults: boolean;
  data: QuizResult[];
}

export const FollowerPresentation = ({
  activeSession,
  title,
  styledTitle,
  label,
  description,
  choices,
  imageUrl,
  timeBasedScoring,
  shouldShowLegacyImage,
  textSizeOffset,
  data,
  isPreviewResults,
}: FollowerPresentationProps) => {
  const { totalCount, currentIndex } = useQuizMetaContext();
  const {
    gameState,
    connectedPlayers,
    respondents,
    joinAsFollower,
    connectedWithDelay,
    timeOffset = 0,
  } = useFollowerQuizGameState();

  const prevActiveSession = useRef(activeSession);
  const createdRef = useRef(false);
  useEffect(() => {
    const sessionChanged = prevActiveSession.current !== activeSession;
    if ((!gameState && !createdRef.current) || sessionChanged) {
      joinAsFollower();
    }
  }, [joinAsFollower, activeSession, gameState]);

  if (isPreviewResults) {
    return (
      <>
        <TitleSection
          title={title}
          styledTitle={styledTitle}
          label={label}
          description={description}
          fontSizeOffset={textSizeOffset}
        />
        <CenteringContainer>
          <QuizBarsDataHandler
            showCorrect={data.length > 0}
            rawResults={data}
            choices={choices}
            enableAnimations
          />
        </CenteringContainer>
      </>
    );
  }

  switch (gameState?.gameState) {
    case GameStateEnums.LOBBY: {
      return (
        <CenteringContainer data-testid="gamestate-lobby">
          <Lobby
            connected={connectedWithDelay}
            total={totalCount}
            current={currentIndex + 1}
            players={connectedPlayers}
          />
        </CenteringContainer>
      );
    }
    case GameStateEnums.GET_READY: {
      return (
        <CenteringContainer>
          <GetReady timeBasedScoring={timeBasedScoring} />
        </CenteringContainer>
      );
    }
    case GameStateEnums.COUNTDOWN: {
      return (
        <>
          <TitleSection
            title={title}
            styledTitle={styledTitle}
            label={label}
            description={description}
            fontSizeOffset={textSizeOffset}
          />
          <CenteringContainer>
            <Countdown
              type="choices"
              respondents={respondents.length}
              everyoneHasVoted={false}
              imageUrl={imageUrl}
              timeOffset={timeOffset}
              startAt={gameState.gameStateData.startAt}
              endAt={gameState.gameStateData.endAt}
              shouldShowLegacyImage={shouldShowLegacyImage}
            >
              <QuizBarsDataHandler
                showCorrect={false}
                choices={choices}
                enableAnimations={false}
                rawResults={[]}
              />
            </Countdown>
          </CenteringContainer>
        </>
      );
    }
    case GameStateEnums.RESULT: {
      return (
        <>
          <TitleSection
            title={title}
            styledTitle={styledTitle}
            label={label}
            description={description}
            fontSizeOffset={textSizeOffset}
          />
          <CenteringContainer mt={4}>
            <QuizBarsDataHandler
              showCorrect
              rawResults={data}
              choices={choices}
              enableAnimations
            />
          </CenteringContainer>
        </>
      );
    }
    case GameStateEnums.ERROR: {
      return (
        <CenteringContainer>
          <Error errorCode={gameState.errorCode} />
        </CenteringContainer>
      );
    }
    default: {
      return (
        <CenteringContainer>
          <Loading
            onReload={() => {
              window.location.reload();
            }}
          />
        </CenteringContainer>
      );
    }
  }
};
